<template>
  <main class="physical-success-page">
    <AppNavBar
      :title="t('card.paymentDetail')"
      :auto-back="false"
      background-color="var(--wp-color-theme)"
      text-color="#ffffff"
      left-arrow-color="#ffffff"
      @click-left="goPendingPage"
    />

    <section class="hero-card">
      <div class="hero-card__banner">
        <div class="hero-card__icon">
          <AppSvgIcon
            name="pay_icon"
            size="100"
            color="var(--wp-color-brand-1)"
          />
        </div>
        <div class="hero-card__text">
          <div class="hero-card__title">{{ t("card.paymentSuccess") }}</div>
          <span>{{ t("card.paymentSuccessDesc") }}</span>
        </div>
      </div>
    </section>

    <section class="content-panel">
      <section class="detail-card">
        <div class="amount-box">
          <div class="amount-box__value">
            <span class="amount-box__number">{{ draft.cardPrice || "0" }}</span>
            <span class="amount-box__symbol">{{
              draft.cardPriceSymbol || "USD"
            }}</span>
          </div>
          <div class="amount-box__label">{{ t("card.paymentFee") }}</div>
        </div>

        <div class="detail-divider" />

        <div v-if="hasCurrentCardInfo" class="detail-row detail-row--first">
          <span>{{ t("wallet.cardNumber") }}</span>
          <div class="detail-value detail-value--card">{{ maskedCardNo }}</div>
        </div>

        <div v-if="hasCurrentCardInfo" class="detail-divider" />

        <div class="detail-row">
          <span>{{ t("card.cardType") }}</span>
          <div class="detail-value">{{ t("card.physicalCard") }}</div>
        </div>

        <div class="detail-divider" />

        <div class="detail-row">
          <span>{{ t("card.recentTradeScene") }}</span>
          <div class="detail-value">{{ t("card.physicalSceneDesc") }}</div>
        </div>
      </section>

      <section class="notice-card">
        <div class="notice-card__icon">
          <AppSvgIcon
            name="icon-12"
            size="20"
            color="var(--wp-color-brand-1)"
          />
        </div>
        <div class="notice-card__content">
          <div class="notice-card__title">
            {{ t("common.friendlyReminder") }}
          </div>
          <p>{{ t("card.physicalCardMakingTip") }}</p>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useCardOrderStore } from "@/stores/card-order";
import { navigateWithState } from "@/utils/route-state";

const { t } = useI18n();
const router = useRouter();
const cardOrderStore = useCardOrderStore();

const draft = ref({});
const currentCardInfo = computed(() => draft.value.currentCardInfo || {});
const hasCurrentCardInfo = computed(
  () => Object.keys(currentCardInfo.value).length > 0,
);
const maskedCardNo = computed(() => {
  const card = currentCardInfo.value;
  return `${card.bin || ""}-****-****-${card.last4 || ""}`.replace(
    /^-\*\*\*\*-\*\*\*\*-/,
    "****-****-****-",
  );
});

function goPendingPage() {
  navigateWithState(router, "/card/physical-pending", {
    namespace: "physical-card-pending",
    payload: {
      from: "PayCardOrderPaymentPage",
    },
  });
}

onMounted(() => {
  cardOrderStore.hydrate();
  draft.value = cardOrderStore.draft || {};
  if (!draft.value?.templateId) {
    navigateWithState(router, "/wallet/open-card", {
      namespace: "card-open",
      replace: true,
      payload: {
        type: "1",
      },
    });
  }
});
</script>

<style scoped lang="scss">
.physical-success-page {
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
}

:deep(.van-nav-bar) {
  --van-nav-bar-height: 56px;
  --van-nav-bar-title-font-size: 18px;
  --van-nav-bar-title-font-weight: 500;
}

.hero-card {
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  height: 190px;
  padding: 20px;
  background: var(--wp-color-theme);
  border-radius: 0 0 50% 50% / 0 0 22px 22px;
}

.hero-card__banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  z-index: 1;
  color: #ffffff;
}

.hero-card__icon {
  flex: 0 0 auto;
}

.hero-card__text {
  flex: 1;
  min-width: 0;

  .hero-card__title,
  span {
    display: block;
  }

  .hero-card__title {
    margin-top: 0;
    font-size: 20px;
    font-weight: 400;
  }

  span {
    margin-top: 4px;
    color: #ededed;
    font-size: 14px;
    line-height: 20px;
  }
}

.content-panel {
  transform: translateY(-60px);
  padding: 0 14px 24px;
}

.detail-card {
  box-sizing: border-box;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #ffffff;
  box-shadow: none;
  overflow: hidden;
}

.amount-box {
  padding: 20px 16px 0;
  text-align: center;

  .amount-box__value,
  .amount-box__label {
    display: block;
  }

  .amount-box__value {
    color: var(--van-primary-color);
    font-weight: 700;
  }

  .amount-box__label {
    margin-top: 4px;
    color: #999999;
    font-size: 14px;
  }
}

.amount-box__number {
  font-size: 24px;
}

.amount-box__symbol {
  margin-left: 4px;
  font-size: 20px;
}

.amount-box__number,
.amount-box__symbol {
  display: inline-block;
  vertical-align: baseline;
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 16px;

  &--first {
    padding-top: 0;
  }

  span,
  .detail-value {
    color: #333333;
    font-size: 14px;
    line-height: 20px;
  }

  .detail-value {
    text-align: right;
    word-break: break-word;
    font-weight: 500;
    color: #000;
  }

  .detail-value--card {
    color: #333333;
    font-weight: 400;
  }
}

.detail-divider {
  height: 1px;
  margin: 20px 16px;
  background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.14) 50%,
      rgba(255, 255, 255, 0) 0
    )
    repeat-x 0 0 / 8px 1px;
}

.detail-row:last-child {
  padding-bottom: 20px;
}

.notice-card {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0 0;
  padding: 16px;
  border: 1px solid var(--wp-color-button-2);
  border-radius: 16px;
  background: var(--wp-color-card-3);
}

.notice-card__icon {
  display: inline-flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--wp-color-card-1);
}

.notice-card__content {
  min-width: 0;

  .notice-card__title,
  p {
    display: block;
  }

  .notice-card__title {
    color: #000000;
    font-size: 16px;
    font-weight: 400;
  }

  p {
    margin: 4px 0 0;
    color: #666666;
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
